"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { devLabel, devLabelPanel } from "@/lib/dev-labels";
import styles from "./ChangelogNotification.module.css";

/**
 * ChangelogNotification — Phase 25-AB Task 9 (panel 34 / oc-cl-notif).
 *
 * Mockup source: docs/mockups/index.html lines 1232-1242.
 * Toast in bottom-left corner. Auto-dismisses after `autoDismissMs`
 * (default 6000ms) unless user closes. Stores "seen" version in
 * localStorage["mml.changelog.seen"] so it only shows once per release.
 */
const STORAGE_KEY = "mml.changelog.seen";

export interface ChangelogNotificationProps {
	version: string;
	title?: string;
	body?: string;
	autoDismissMs?: number;
	onDismiss?: () => void;
	/** If true, ignores the localStorage seen-check (dev page). */
	forceShow?: boolean;
}

export function ChangelogNotification({
	version,
	title = "새로운 기능",
	body = "OpenCut 33 항목 통합 · AI Magic Wand 50종 · Live Dashboard",
	autoDismissMs = 6000,
	onDismiss,
	forceShow = false,
}: ChangelogNotificationProps) {
	const [open, setOpen] = useState(false);
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
		if (forceShow) {
			setOpen(true);
			return;
		}
		try {
			const seen = window.localStorage.getItem(STORAGE_KEY);
			if (seen !== version) setOpen(true);
		} catch {
			setOpen(true);
		}
	}, [version, forceShow]);

	useEffect(() => {
		if (!open || autoDismissMs <= 0) return;
		const t = window.setTimeout(() => close(), autoDismissMs);
		return () => window.clearTimeout(t);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, autoDismissMs]);

	function close() {
		setOpen(false);
		try {
			window.localStorage.setItem(STORAGE_KEY, version);
		} catch {
			/* quota — ignore */
		}
		onDismiss?.();
	}

	if (!open || !mounted) return null;

	return createPortal(
		<div
			id="oc-cl-notif"
			className={styles.notif}
			role="status"
			aria-live="polite"
			{...devLabelPanel("34 · ChangelogNotification")}
		>
			<div className={styles.header} {...devLabel("34-A")}>
				<span className={styles.version}>v{version}</span>
				<span className={styles.title}>{title}</span>
				<button
					type="button"
					className={styles.close}
					onClick={close}
					aria-label="닫기"
					{...devLabel("34-B")}
				>
					×
				</button>
			</div>
			<div className={styles.body} {...devLabel("34-C")}>
				{body}
			</div>
			<button
				type="button"
				className={styles.cta}
				onClick={close}
				{...devLabel("34-D")}
			>
				전체 changelog →
			</button>
			<div style={{ clear: "both" }} />
		</div>,
		document.body,
	);
}
